import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BsFillBagFill } from "react-icons/bs";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { AiFillStar, AiOutlineClockCircle, AiOutlineStar } from "react-icons/ai";
import { MdOutlineLocalShipping } from "react-icons/md";
import { RxCross1 } from "react-icons/rx";
import { backend_url, server } from "../server";
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from "../uploadLimits";
import { getAllOrdersOfUser } from "../redux/actions/order";
import { useMarkNotificationsReadOnPage } from "../hooks/useMarkNotificationsReadOnPage";
import Ratings from "./Products/Ratings";
import {
  getBuyerOrderBreakdown,
  bundlePostageLabelForBreakdown,
  getLineOriginalUnitPrice,
  getLinePaidUnitPrice,
} from "../utils/orderPriceBreakdown";
import {
  getEstimatedDeliveryDaysNumber,
  getOrderEstimatedArrivalDisplay,
  getOrderPlacedAtFormatted,
  buyerDispatchDelayChoicePending,
  buyerChoseToWaitForDispatch,
  isOrderCancelled,
} from "../utils/orderEta";
import { addDays, addWorkingDays } from "../utils/workingDays";
import {
  isOrderCompleted,
  getEffectiveRefundAwareStatus,
  orderHasActiveRefundCase,
  orderRefundRequestInProgress,
  orderIssueReportedOnly,
  orderPartialRefundSettled,
  orderRefundCaseIsClosed,
  productLineHasRefundRecord,
  ACTIVE_REFUND_STATUSES,
} from "../utils/orderListStatus";
import MobileFriendlyModal, { MODAL_TEXTAREA_CLASS } from "./Layout/MobileFriendlyModal";
import { useScrollToRefOnMobile } from "../utils/navigateWithMobileDashboardScroll";

const REFUND_CATEGORIES = ["Not as advertised", "Damaged", "No longer needed", "Other"];
const isVaMediationInProgress = (status) => status === "open" || status === "mediating";
const ESCROW_HOLD_REFUND_STATUSES = [
  "Processing refund",
  "Refund Offer Pending",
  "Return Required",
  "Return in Transit",
  "Refund Awaiting Seller Confirmation",
  "Refund Rejected",
];

const OPEN_REFUND_CASE_STATUSES = ACTIVE_REFUND_STATUSES;

const refundImageSrc = (img) => {
  const path = typeof img === "string" ? img.trim() : "";
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${backend_url}/${path.replace(/^\//, "")}`;
};

const RefundEvidenceThumbnails = ({ images }) => {
  const urls = (Array.isArray(images) ? images : [])
    .map(refundImageSrc)
    .filter(Boolean);
  if (!urls.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {urls.map((src, idx) => (
        <a
          key={`${src}-${idx}`}
          href={src}
          target="_blank"
          rel="noreferrer"
          className="block overflow-hidden rounded-lg border border-slate-200 bg-white"
        >
          <img
            src={src}
            alt={`Refund evidence ${idx + 1}`}
            className="h-20 w-20 object-cover"
          />
        </a>
      ))}
    </div>
  );
};

const ALLOWED_REFUND_IMAGE_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".jif",
  ".jfif",
  ".webp",
  ".avif",
  ".gif",
  ".svg",
];

const formatCurrency = (value) => {
  if (!value && value !== 0) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(value));
};

const primaryButtonClasses =
  "inline-flex h-11 items-center justify-center rounded-xl bg-[#38513b] px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4232] disabled:cursor-not-allowed disabled:bg-slate-400";
const secondaryButtonClasses =
  "inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-6 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#38513b] hover:text-[#38513b]";

const getRefundStatusLabel = (status) => {
  if (status === "Processing refund") return "Under review";
  if (status === "Refund Offer Pending") return "Partial refund offered";
  if (status === "Return Required") return "Return required";
  if (status === "Return in Transit") return "Return in transit";
  if (status === "Refund Awaiting Seller Confirmation") return "Awaiting seller confirmation";
  if (status === "Refund Success") return "Refund credited";
  if (status === "Refund Rejected") return "Declined";
  if (status === "Refund Resolved") return "Resolved (no refund)";
  return status || "—";
};

const UserOrderDetails = () => {
  const { orders, isLoading } = useSelector((state) => state.order);
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();
  const pageTopRef = useRef(null);
  useScrollToRefOnMobile(pageTopRef, id);
  useMarkNotificationsReadOnPage("order_detail");
  const userId = user?._id;

  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [isSellerReviewModalOpen, setIsSellerReviewModalOpen] = useState(false);
  const [sellerReviewRating, setSellerReviewRating] = useState(0);
  const [sellerReviewComment, setSellerReviewComment] = useState("");
  const [isSubmittingSellerReview, setIsSubmittingSellerReview] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  /** null = choose mode (multi-line orders only); "item" | "full" */
  const [refundRequestMode, setRefundRequestMode] = useState(null);
  const [refundReason, setRefundReason] = useState("");
  const [selectedProductsForRefund, setSelectedProductsForRefund] = useState([]);
  const [refundImages, setRefundImages] = useState({}); // productId -> array of image files
  const [fullOrderRefundImages, setFullOrderRefundImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  /** Buyer dispute path: refund (money back) vs return (willing to post item back). */
  const [buyerDisputeKind, setBuyerDisputeKind] = useState("refund");
  const [returnCourier, setReturnCourier] = useState("");
  const [returnCode, setReturnCode] = useState("");
  const [returnLink, setReturnLink] = useState("");
  const [markingReturn, setMarkingReturn] = useState(false);
  const [offerResponseLoading, setOfferResponseLoading] = useState(false);
  const [showRefundActions, setShowRefundActions] = useState(false);
  const [vaEligibility, setVaEligibility] = useState(null);
  const [vaNote, setVaNote] = useState("");
  const [vaSubmitting, setVaSubmitting] = useState(false);
  const [vaInboxLink, setVaInboxLink] = useState(null);
  const [vaAppealNote, setVaAppealNote] = useState("");
  const [vaAppealing, setVaAppealing] = useState(false);
  const [dispatchDelaySubmitting, setDispatchDelaySubmitting] = useState(false);
  const [reviewExchange, setReviewExchange] = useState(null);

  const validateRefundImageFiles = (files) => {
    const list = Array.from(files || []);
    if (!list.length) return [];

    const oversized = list.filter((file) => file.size > IMAGE_UPLOAD_MAX_BYTES);
    if (oversized.length > 0) {
      toast.error(
        `Some images exceed ${IMAGE_UPLOAD_MAX_MB}MB. Please choose smaller files.`
      );
      return null;
    }

    const invalidType = list.find((file) => {
      const lower = String(file?.name || "").toLowerCase();
      return !ALLOWED_REFUND_IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
    });
    if (invalidType) {
      toast.error(
        `Unsupported image type (${invalidType.name}). Allowed: ${ALLOWED_REFUND_IMAGE_EXTENSIONS.join(", ")}`
      );
      return null;
    }

    return list;
  };

  useEffect(() => {
    if (userId) {
      dispatch(getAllOrdersOfUser(userId));
    }
  }, [dispatch, userId]);

  // Refetch orders when user returns to the tab (e.g. after notification) so status updates are visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && userId) {
        dispatch(getAllOrdersOfUser(userId));
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
    };
  }, [dispatch, userId]);

  const order = useMemo(
    () => orders?.find((item) => String(item._id) === String(id)),
    [orders, id]
  );

  const fetchReviewExchange = useCallback(async () => {
    if (!order?._id || !isOrderCompleted(order)) {
      setReviewExchange(null);
      return;
    }
    try {
      const { data } = await axios.get(`${server}/order/review-exchange/${order._id}`, {
        withCredentials: true,
      });
      if (data.success) setReviewExchange(data.exchange);
    } catch {
      setReviewExchange(null);
    }
  }, [order?._id, order?.userReceivedConfirmed, order?.payoutReleased]);

  const clearReviewQueryParam = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("review") !== "1") return;
    params.delete("review");
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    fetchReviewExchange();
  }, [fetchReviewExchange]);

  useEffect(() => {
    if (!order || !isOrderCompleted(order)) return;
    const params = new URLSearchParams(location.search);
    if (params.get("review") !== "1") return;
    const section = document.getElementById("order-reviews");
    if (section) {
      setTimeout(() => section.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }
    if (order.sellerReviewed) return;
    if (reviewExchange && !reviewExchange.youSubmitted) {
      setSellerReviewRating(0);
      setSellerReviewComment("");
      setIsSellerReviewModalOpen(true);
    }
  }, [order, location.search, reviewExchange]);

  const hasOpenRefundCase = useMemo(() => {
    if (!order) return false;
    if (isVaMediationInProgress(order.vaMediation?.status)) return true;
    return orderRefundRequestInProgress(order);
  }, [order]);

  const refundCaseClosed = useMemo(() => orderRefundCaseIsClosed(order), [order]);

  const partialRefundSettled = useMemo(() => orderPartialRefundSettled(order), [order]);

  const effectiveRefundStatus = useMemo(
    () => getEffectiveRefundAwareStatus(order),
    [order]
  );

  const pendingPartialOffers = useMemo(
    () => (order?.refunds || []).filter((r) => r.status === "Refund Offer Pending"),
    [order?.refunds]
  );

  const legacyPartialOfferAmount = useMemo(() => {
    if (order?.status !== "Refund Offer Pending") return 0;
    return Number(order?.partialRefundOffer?.amount || 0);
  }, [order?.status, order?.partialRefundOffer]);

  const acceptedPartialRefundTotal = useMemo(() => {
    if (!order) return 0;
    const fromLines = (order.refunds || [])
      .filter((r) => r.status === "Refund Success" && r.partialRefundAccepted)
      .reduce((sum, r) => sum + Number(r.refundAmount || 0), 0);
    const fromOrder = Number(order.partialRefundAcceptedAmount || 0);
    return Number((fromLines > 0 ? fromLines : fromOrder).toFixed(2));
  }, [order]);

  const respondToPartialOffer = async (action, refundId = null) => {
    if (!order?._id) return;
    setOfferResponseLoading(true);
    try {
      await axios.put(
        `${server}/order/order-refund-offer-response/${order._id}`,
        { action, ...(refundId ? { refundId } : {}) },
        { withCredentials: true }
      );
      toast.success(
        action === "accept"
          ? "Partial refund accepted and credited to your wallet."
          : "Partial refund offer declined."
      );
      if (userId) dispatch(getAllOrdersOfUser(userId));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update refund offer.");
    } finally {
      setOfferResponseLoading(false);
    }
  };

  useEffect(() => {
    // Reset the issue/refund actions panel when switching orders.
    setShowRefundActions(false);
  }, [order?._id]);

  const payoutBlockedByRefund = useMemo(() => {
    if (!order) return false;
    if (partialRefundSettled) return false;
    if (ESCROW_HOLD_REFUND_STATUSES.includes(order.status)) return true;
    if (
      Array.isArray(order.refunds) &&
      order.refunds.some((r) => ESCROW_HOLD_REFUND_STATUSES.includes(r.status))
    ) {
      return true;
    }
    if (
      order.buyerReportedIssueAt &&
      !order.payoutReleased &&
      !order.userReceivedConfirmed &&
      order.vaMediation?.status !== "resolved"
    ) {
      return true;
    }
    return false;
  }, [order, partialRefundSettled]);

  const isBuyer = useMemo(() => {
    if (!order || !userId) return false;
    return String(order.user?._id || order.user) === String(userId);
  }, [order, userId]);

  const canBuyerWithdrawRefund = useMemo(() => {
    if (!order || !isBuyer) return false;
    if (partialRefundSettled) return false;
    if (isVaMediationInProgress(order.vaMediation?.status)) return false;
    if (order.refunds?.length > 0) {
      return order.refunds.some((r) => r.status === "Processing refund");
    }
    if (
      order.buyerReportedIssueAt &&
      !order.payoutReleased &&
      !order.userReceivedConfirmed
    ) {
      return true;
    }
    return order.status === "Processing refund" && String(order.refundReason || "").trim();
  }, [order, isBuyer, partialRefundSettled]);

  const localVaEligibility = useMemo(() => {
    if (!order) return null;

    let disputeStartedAt = null;
    if (Array.isArray(order.refunds) && order.refunds.length > 0) {
      order.refunds.forEach((refund) => {
        const raw = refund?.requestedAt || refund?.createdAt;
        if (!raw) return;
        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) return;
        if (!disputeStartedAt || date < disputeStartedAt) disputeStartedAt = date;
      });
    }

    if (order.refundRequestedAt) {
      const date = new Date(order.refundRequestedAt);
      if (!Number.isNaN(date.getTime()) && (!disputeStartedAt || date < disputeStartedAt)) {
        disputeStartedAt = date;
      }
    }

    if (order.buyerReportedIssueAt) {
      const date = new Date(order.buyerReportedIssueAt);
      if (!Number.isNaN(date.getTime()) && (!disputeStartedAt || date < disputeStartedAt)) {
        disputeStartedAt = date;
      }
    }

    if (!disputeStartedAt && order.status === "Processing refund" && order.createdAt) {
      const date = new Date(order.createdAt);
      if (!Number.isNaN(date.getTime())) disputeStartedAt = date;
    }

    const refunds = Array.isArray(order.refunds) ? order.refunds : [];
    const hasRefunds = refunds.length > 0;
    const allRefundsSuccess = hasRefunds && refunds.every((r) => r.status === "Refund Success");
    const allRefundsResolved = hasRefunds && refunds.every((r) => r.status === "Refund Resolved");
    if (orderPartialRefundSettled(order)) {
      return {
        disputeStartedAt: null,
        waitUntil: null,
        canEscalate: false,
        vaMediation: order.vaMediation || { status: "none" },
      };
    }

    // Order completed — no new VA escalation.
    if (order.userReceivedConfirmed || order.payoutReleased) {
      return {
        disputeStartedAt: null,
        waitUntil: null,
        canEscalate: false,
        vaMediation: order.vaMediation || { status: "none" },
      };
    }

    const hasActiveRefundDispute =
      (!allRefundsSuccess && !allRefundsResolved && hasRefunds) ||
      Boolean(order.refundRequestedAt) ||
      (Boolean(String(order.refundReason || "").trim()) && order.status !== "Refund Resolved") ||
      ["Processing refund", "Return Required", "Return in Transit", "Refund Awaiting Seller Confirmation", "Refund Rejected"].includes(order.status || "") ||
      (Boolean(order.buyerReportedIssueAt) &&
        !order.payoutReleased &&
        !order.userReceivedConfirmed &&
        order.vaMediation?.status !== "resolved");

    const waitMs = 3 * 24 * 60 * 60 * 1000;
    const waitUntil = disputeStartedAt ? new Date(disputeStartedAt.getTime() + waitMs) : null;
    const canEscalate = Boolean(disputeStartedAt && hasActiveRefundDispute && Date.now() >= waitUntil.getTime());

    return {
      disputeStartedAt,
      waitUntil,
      canEscalate,
      vaMediation: order.vaMediation || { status: "none" },
    };
  }, [order]);

  const effectiveVaEligibility = vaEligibility || localVaEligibility;

  /** Buyer has raised (or is in) a refund/dispute — show VA escalation panel, not generic support. */
  const buyerHasRefundDisputeTrack = useMemo(() => {
    if (!order) return false;
    if (isVaMediationInProgress(order.vaMediation?.status) || order.vaMediation?.status === "resolved") {
      return true;
    }
    if (order.userReceivedConfirmed || order.payoutReleased) return false;
    if (order.refunds?.length > 0) return true;
    if (order.refundRequestedAt) return true;
    if (order.buyerReportedIssueAt && !order.payoutReleased && !order.userReceivedConfirmed) {
      return true;
    }
    if (String(order.refundReason || "").trim() && order.status !== "Refund Resolved") {
      return true;
    }
    return [
      "Processing refund",
      "Refund Offer Pending",
      "Return Required",
      "Return in Transit",
      "Refund Awaiting Seller Confirmation",
      "Refund Rejected",
    ].includes(order.status || "");
  }, [order]);

  const showVaDisputeSection = useMemo(() => {
    if (!isBuyer || !order) return false;
    if (partialRefundSettled || effectiveRefundStatus === "Refund Success") return false;
    if (
      effectiveRefundStatus === "Refund Resolved" &&
      !isVaMediationInProgress(order.vaMediation?.status) &&
      order.vaMediation?.status !== "resolved"
    ) {
      return false;
    }
    // Completed order: only keep the section if VA already has an active/past case to show.
    if (order.userReceivedConfirmed || order.payoutReleased) {
      return (
        isVaMediationInProgress(order.vaMediation?.status) ||
        order.vaMediation?.status === "resolved"
      );
    }
    return buyerHasRefundDisputeTrack;
  }, [
    isBuyer,
    order,
    partialRefundSettled,
    effectiveRefundStatus,
    buyerHasRefundDisputeTrack,
  ]);

  useEffect(() => {
    if (!order?._id || !isBuyer) {
      setVaEligibility(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${server}/order/va-escalation-eligibility/${order._id}`, {
          withCredentials: true,
        });
        if (!cancelled) setVaEligibility(data);
      } catch {
        if (!cancelled) setVaEligibility(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    order?._id,
    isBuyer,
    order?.vaMediation?.status,
    order?.userReceivedConfirmed,
    order?.payoutReleased,
  ]);

  useEffect(() => {
    if (!order?._id || !isBuyer || order.vaMediation?.status !== "mediating") {
      setVaInboxLink(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${server}/order/va-mediation-inbox/${order._id}`, {
          withCredentials: true,
        });
        if (!cancelled && data?.canMessage && data?.inboxLink) {
          setVaInboxLink(data.inboxLink);
        } else if (!cancelled) {
          setVaInboxLink(null);
        }
      } catch {
        if (!cancelled) setVaInboxLink(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order?._id, isBuyer, order?.vaMediation?.status]);

  const orderItems = order?.cart || [];
  const orderLineCount = orderItems.length;

  const openRefundModal = useCallback((disputeKind = "refund") => {
    setRefundRequestMode(orderLineCount <= 1 ? "full" : null);
    setSelectedProductsForRefund([]);
    setRefundImages({});
    setRefundReason("");
    setFullOrderRefundImages([]);
    setBuyerDisputeKind(disputeKind === "return" ? "return" : "refund");
    setIsRefundModalOpen(true);
  }, [orderLineCount]);

  const totalQuantity = orderItems.reduce((acc, item) => acc + item.qty, 0);
  const priceBreakdown = useMemo(() => getBuyerOrderBreakdown(order), [order]);
  const trackingStatus = order?.trackingStatus || "pending";
  const trackingCode = order?.trackingCode || "";
  const trackingCourier = order?.trackingCourier || "";
  const trackingLink = order?.trackingLink || "";
  const trackingLinkHref = (() => {
    const raw = String(trackingLink).trim();
    if (!raw) return "";
    return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  })();
  const hasTrackingInfo = Boolean(
    String(trackingCode).trim() || String(trackingCourier).trim() || String(trackingLink).trim()
  );
  const fulfilledByName =
    orderItems[0]?.shop?.name ||
    orderItems[0]?.shop?.shopName ||
    order?.shop?.name ||
    "vendor";
  const userReceivedConfirmed = Boolean(order?.userReceivedConfirmed);

  // Estimated delivery window:
  // - Estimated delivery date = createdAt start-of-day + estimatedDeliveryDays
  // - Customer can confirm for 2 additional days (end-of-day of +2)
  const {
    estimatedDeliveryDate,
    estimatedDeliveryDateFormatted,
    estimatedDeliveryEndDayStart,
    estimatedDeliveryEndDayFormatted,
    estimatedDeliveryPassed,
    confirmWindowEnded,
    withinConfirmWindow,
  } = useMemo(() => {
    const result = {
      estimatedDeliveryDate: null,
      estimatedDeliveryDateFormatted: "",
      estimatedDeliveryEndDayStart: null,
      estimatedDeliveryEndDayFormatted: "",
      estimatedDeliveryPassed: false,
      confirmWindowEnded: false,
      withinConfirmWindow: false,
    };

    const days = getEstimatedDeliveryDaysNumber(order);
    if (days == null) return result;
    if (!order?.dispatchedAt) return result;

    const referenceDateRaw = order.dispatchedAt;

    const referenceDate = new Date(referenceDateRaw);
    referenceDate.setHours(0, 0, 0, 0);

    const estDate = addWorkingDays(referenceDate, days);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const endDayStart = addDays(estDate, 2);

    result.estimatedDeliveryDate = estDate;
    result.estimatedDeliveryDateFormatted = estDate.toLocaleDateString(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    result.estimatedDeliveryEndDayStart = endDayStart;
    result.estimatedDeliveryEndDayFormatted = endDayStart.toLocaleDateString(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    result.estimatedDeliveryPassed = today >= estDate;
    result.confirmWindowEnded = today > endDayStart;
    result.withinConfirmWindow = today >= estDate && today <= endDayStart;

    return result;
  }, [
    order?.estimatedDeliveryDays,
    order?.dispatchedAt,
    order?.dispatchedAt,
  ]);

  const hasEstimatedDelivery = Boolean(getEstimatedDeliveryDaysNumber(order) && estimatedDeliveryDate);
  const overviewEtaFormatted = order ? getOrderEstimatedArrivalDisplay(order) : "";
  const overviewHasDeliveryDays = getEstimatedDeliveryDaysNumber(order) != null;
  const refundCredited = effectiveRefundStatus === "Refund Success";
  const canConfirmReceiptNow =
    !refundCredited &&
    !order?.payoutReleased &&
    Boolean(order?.dispatchedAt) &&
    (Boolean(order?.status === "Delivered") ||
      (hasEstimatedDelivery && withinConfirmWindow && !userReceivedConfirmed));

  // Refund window follows the same estimated-delivery + 2 days policy.
  // Legacy fallback: if we can't compute the estimated window, use the old delivered/confirmation window.
  const canRequestRefund = useMemo(() => {
    if (order?.userReceivedConfirmed || order?.payoutReleased) {
      return false;
    }
    if (partialRefundSettled || refundCaseClosed) return false;
    if (orderIssueReportedOnly(order)) return true;
    if (hasOpenRefundCase) return false;
    if (order?.refundRequestedAt) return false;
    if (hasEstimatedDelivery && estimatedDeliveryPassed && !confirmWindowEnded) {
      return true;
    }

    const referenceDateRaw = order?.deliveredAt || order?.createdAt;
    if (!referenceDateRaw) return false;
    const orderDate = new Date(referenceDateRaw);
    const now = new Date();
    const daysDiff = (now - orderDate) / (1000 * 60 * 60 * 24);
    return daysDiff <= 2;
  }, [
    hasEstimatedDelivery,
    estimatedDeliveryPassed,
    confirmWindowEnded,
    partialRefundSettled,
    hasOpenRefundCase,
    refundCaseClosed,
    order?.userReceivedConfirmed,
    order?.payoutReleased,
    order?.refundRequestedAt,
    order?.deliveredAt,
    order?.createdAt,
  ]);

  const refundRequestLockedReason = useMemo(() => {
    if (!order) return "";
    if (refundCaseClosed) {
      if (effectiveRefundStatus === "Refund Success") {
        return "A refund was already approved or credited for this order.";
      }
      if (effectiveRefundStatus === "Refund Resolved") {
        return "This refund request was closed (cancelled or resolved).";
      }
      if (effectiveRefundStatus === "Refund Rejected") {
        return "This refund request was declined. You cannot submit another here.";
      }
      return "This refund case is closed.";
    }
    if (hasOpenRefundCase) {
      return "A refund case is already open on this order.";
    }
    return "";
  }, [order, refundCaseClosed, hasOpenRefundCase, effectiveRefundStatus]);

  useEffect(() => {
    if (!order?._id) return;
    const params = new URLSearchParams(location.search || "");
    if (params.get("raiseIssue") !== "1") return;
    if (canRequestRefund) {
      openRefundModal();
    } else {
      toast.info("Refund requests are available only during the confirmation window.");
    }
  }, [location.search, order?._id, canRequestRefund, openRefundModal]);

  const openReviewModal = (item) => {
    setSelectedItem(item);
    setRating(item?.existingRating || 0);
    setComment("");
    setIsReviewOpen(true);
  };

  const closeReviewModal = () => {
    setIsReviewOpen(false);
    setSelectedItem(null);
    setRating(0);
    setComment("");
  };

  const openSellerReviewModal = () => {
    setSellerReviewRating(0);
    setSellerReviewComment("");
    setIsSellerReviewModalOpen(true);
  };

  const closeSellerReviewModal = () => {
    setIsSellerReviewModalOpen(false);
    setSellerReviewRating(0);
    setSellerReviewComment("");
  };

  const submitSellerReview = async () => {
    if (!order?._id) return;
    if (!sellerReviewRating) {
      toast.info("Select a rating for the seller.");
      return;
    }
    setIsSubmittingSellerReview(true);
    try {
      await axios.post(
        `${server}/order/create-seller-review/${order._id}`,
        { rating: sellerReviewRating, comment: sellerReviewComment },
        { withCredentials: true }
      );
      toast.success("Thanks for rating this seller!");
      closeSellerReviewModal();
      clearReviewQueryParam();
      setReviewExchange((prev) => (prev ? { ...prev, youSubmitted: true } : prev));
      if (userId) {
        dispatch(getAllOrdersOfUser(userId));
      }
      fetchReviewExchange();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "We couldn't save your seller review. Please try again."
      );
    } finally {
      setIsSubmittingSellerReview(false);
    }
  };

  const submitReview = async () => {
    if (!selectedItem) return;
    if (!rating) {
      toast.info("Select a rating to submit your review.");
      return;
    }

    const payload = {
      user,
      rating,
      comment,
      productId: selectedItem?._id,
      orderId: id,
    };

    setIsSubmittingReview(true);
    try {
      await axios.put(`${server}/product/create-new-review`, payload, {
        withCredentials: true,
      });

      // Attempt event review if item belongs to an event
      if (selectedItem?.isEvent || selectedItem?.eventId) {
        await axios.put(`${server}/event/create-new-review-event`, payload, {
          withCredentials: true,
        });
      }

      toast.success("Thanks for sharing your review!");
      dispatch(getAllOrdersOfUser(userId));
      closeReviewModal();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "We couldn't save your review. Please try again."
      );
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleContactSeller = async (item) => {
    if (!userId) {
      toast.error("Please sign in to contact the seller.");
      return;
    }

    const sellerId =
      item?.shopId || item?.shop?._id || item?.sellerId || order?.sellerId;

    if (!sellerId) {
      toast.error("Seller details not available for this product.");
      return;
    }

    // Prevent users from messaging themselves
    if (userId && sellerId && String(userId) === String(sellerId)) {
      toast.error("You cannot message yourself");
      return;
    }

    if (order?.conversationId) {
      navigate(`/profile/inbox?conversation=${order.conversationId}`, { replace: false });
      return;
    }

    const groupTitle = `${sellerId}_${userId}`;

    try {
      const res = await axios.post(
        `${server}/conversation/create-new-conversation`,
        {
          groupTitle,
          userId,
          sellerId,
        },
        { withCredentials: true }
      );

      const conversationId =
        res.data?.existingConversation?._id ||
        res.data?.conversation?._id ||
        res.data?._id;

      if (!conversationId) {
        throw new Error("Unable to determine conversation");
      }

      navigate(`/profile/inbox?conversation=${conversationId}`, { replace: false });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "We couldn't connect you to the seller right now."
      );
    }
  };

  const uploadRefundImages = async (productId, files) => {
    if (!files || files.length === 0) return [];
    
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append("images", file);
    });

    try {
      const { data } = await axios.post(
        `${server}/message/upload-refund-image`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );
      return Array.isArray(data?.images) ? data.images : (data?.image ? [data.image] : [data?.filename].filter(Boolean));
    } catch (error) {
      console.error("Error uploading refund images:", error);
      toast.error(
        error?.response?.data?.message ||
          "Failed to upload images. Please check file type/size and try again."
      );
      return [];
    }
  };

  const handleVaEscalation = async () => {
    if (!order?._id || !userId) return;
    setVaSubmitting(true);
    try {
      await axios.post(
        `${server}/order/va-escalate/${order._id}`,
        { buyerNote: vaNote.trim() },
        { withCredentials: true }
      );
      toast.success("Veteran Airsoft has been notified. Our team will review this dispute.");
      setVaNote("");
      dispatch(getAllOrdersOfUser(userId));
      const { data } = await axios.get(`${server}/order/va-escalation-eligibility/${order._id}`, {
        withCredentials: true,
      });
      setVaEligibility(data);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not submit your request.");
    } finally {
      setVaSubmitting(false);
    }
  };

  const handleVaAppeal = async () => {
    if (!order?._id || !userId || !vaAppealNote.trim()) {
      toast.error("Please explain why you are appealing this decision.");
      return;
    }
    setVaAppealing(true);
    try {
      await axios.post(
        `${server}/order/va-appeal/${order._id}`,
        { appealNote: vaAppealNote.trim() },
        { withCredentials: true }
      );
      toast.success("Your appeal has been submitted. Veteran Airsoft will review again.");
      setVaAppealNote("");
      dispatch(getAllOrdersOfUser(userId));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not submit your appeal.");
    } finally {
      setVaAppealing(false);
    }
  };

  const canAppealVaDecision = useMemo(() => {
    const vm = order?.vaMediation;
    if (!vm || vm.status !== "resolved" || vm.appealedAt) return false;
    if (!vm.appealDeadline) return true;
    return new Date(vm.appealDeadline) > new Date();
  }, [order?.vaMediation]);

  const showDispatchDelayChoice = useMemo(
    () => buyerDispatchDelayChoicePending(order),
    [order]
  );

  const buyerWaitingForDispatch = useMemo(
    () => buyerChoseToWaitForDispatch(order),
    [order]
  );

  const orderCancelled = useMemo(() => isOrderCancelled(order), [order]);

  const handleDispatchDelayChoice = async (choice) => {
    if (!order?._id || !userId) return;
    setDispatchDelaySubmitting(true);
    try {
      const { data } = await axios.post(
        `${server}/order/dispatch-delay-choice/${order._id}`,
        { choice },
        { withCredentials: true }
      );
      toast.success(data?.message || (choice === "cancel" ? "Order cancelled." : "Thanks for waiting."));
      dispatch(getAllOrdersOfUser(userId));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to save your choice.");
    } finally {
      setDispatchDelaySubmitting(false);
    }
  };

  const requestRefund = async (mode = "auto") => {
    if (!order) return;
    
    if (!canRequestRefund) {
      toast.error("Refunds can only be requested within 2 days after the estimated delivery date");
      return;
    }

    const useSelectedItems =
      mode === "selected" ? true : mode === "full" ? false : selectedProductsForRefund.length > 0;

    // Product-specific refunds
    if (useSelectedItems) {
      if (selectedProductsForRefund.length === 0) {
        toast.error("Please select at least one item to refund.");
        return;
      }

      // Product-specific refunds
      const productRefunds = [];
      
      for (const productRefund of selectedProductsForRefund) {
        const { productId, productName, quantity, reasonCategory, reason } = productRefund;
        
        if (!reason || reason.trim().length < 10) {
          toast.error(`Please provide a message (min 10 characters) for refunding ${productName}`);
          return;
        }
        if (!refundImages[productId] || refundImages[productId].length === 0) {
          toast.error(`Please upload at least one image for ${productName}`);
          return;
        }

        // Upload images for this product refund
        let imageUrls = [];
        if (refundImages[productId] && refundImages[productId].length > 0) {
          setUploadingImages(true);
          try {
            imageUrls = await uploadRefundImages(productId, refundImages[productId]);
          } finally {
            setUploadingImages(false);
          }
        }

        productRefunds.push({
          productId,
          productName,
          quantity,
          reasonCategory: reasonCategory || "Other",
          reason: reason.trim(),
          images: imageUrls,
          buyerDisputeKind,
        });
      }

      setIsRefunding(true);
      try {
        await axios.put(
          `${server}/order/order-refund/${id}`,
          { productRefunds },
          { withCredentials: true }
        );
        toast.success("Refund request submitted successfully.");
        setIsRefundModalOpen(false);
        setRefundRequestMode(null);
        setSelectedProductsForRefund([]);
        setRefundImages({});
        setFullOrderRefundImages([]);
        setRefundReason("");
        dispatch(getAllOrdersOfUser(userId));
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "Unable to submit refund request right now."
        );
      } finally {
        setIsRefunding(false);
      }
    } else {
      // Legacy whole-order refund
      if (!refundReason || refundReason.trim() === "") {
        toast.error("Please provide a reason for the refund request");
        return;
      }
      if (refundReason.trim().length < 10) {
        toast.error("Please provide at least 10 characters describing the issue.");
        return;
      }
      if (!fullOrderRefundImages.length) {
        toast.error("Please upload at least one image with your refund request.");
        return;
      }

      setUploadingImages(true);
      let imageUrls = [];
      try {
        imageUrls = await uploadRefundImages("full-order", fullOrderRefundImages);
      } finally {
        setUploadingImages(false);
      }
      if (!imageUrls.length) {
        toast.error("Please upload at least one image with your refund request.");
        return;
      }

      setIsRefunding(true);
      try {
        await axios.put(
          `${server}/order/order-refund/${id}`,
          {
            status: "Processing refund",
            refundReason: refundReason.trim(),
            refundImages: imageUrls,
            buyerDisputeKind,
          },
          { withCredentials: true }
        );
        toast.success("Refund request submitted successfully.");
        setIsRefundModalOpen(false);
        setRefundRequestMode(null);
        setRefundReason("");
        setFullOrderRefundImages([]);
        dispatch(getAllOrdersOfUser(userId));
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "Unable to submit refund request right now."
        );
      } finally {
        setIsRefunding(false);
      }
    }
  };

  return (
    <div ref={pageTopRef} className="min-h-[70vh]  py-10">
      <div className="mx-auto max-w-5xl px-4">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#38513b]/10 text-[#38513b]">
              <BsFillBagFill size={20} />
            </span>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">
                Order details
              </h1>
              <p className="text-sm text-slate-500">
                Track delivery, share feedback, or request help for this order.
              </p>
            </div>
          </div>

          {order && !isOrderCancelled(order) && (
            <div className="flex flex-wrap gap-3">
              <Link
                to={`/user/track/order/${String(order._id)}`}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#38513b] hover:text-[#38513b]"
              >
                Track this order
              </Link>
            </div>
          )}
        </header>

        {isLoading && !order && (
          <div className="mt-10 space-y-4">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="h-24 animate-pulse rounded-2xl bg-slate-200" />
            ))}
          </div>
        )}

        {!isLoading && !order && (
          <div className="mt-12 rounded-3xl border border-dashed border-slate-200 bg-white/60 p-12 text-center shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">
              Order not found
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              We couldn’t locate this order under your account. Double-check the
              link or reach out to support.
            </p>
            <Link to="/" className={`${primaryButtonClasses} mt-6`}>
              Back to shopping
            </Link>
          </div>
        )}

        {order && (
          <div className="mt-8 space-y-6">
            <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Order overview
                  </h2>
                  <p className="text-sm text-slate-500">
                    Order ID{" "}
                    <span className="font-semibold text-slate-900">
                      #{order?.orderNumber ?? order?._id?.toString().substring(0, 8) ?? "N/A"}
                    </span>
                  </p>
                </div>
                <div className="text-right text-sm text-slate-500">
                  {orderCancelled ? (
                    <p className="mb-2">
                      <span className="inline-flex items-center rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
                        Cancelled
                      </span>
                    </p>
                  ) : null}
                  <p className="flex items-center justify-end gap-1">
                    <AiOutlineClockCircle size={16} className="shrink-0 text-slate-400" aria-hidden />
                    <span>
                      Placed on{" "}
                      <span className="font-semibold text-slate-900">
                        {getOrderPlacedAtFormatted(order) || "—"}
                      </span>
                    </span>
                  </p>
                  {!orderCancelled ? (
                  <p className="mt-1 flex flex-col items-end gap-0.5 sm:flex-row sm:items-center sm:justify-end sm:gap-1">
                    <span className="inline-flex items-center gap-1">
                      <MdOutlineLocalShipping size={16} className="shrink-0 text-slate-400" aria-hidden />
                      <span>
                        Estimated arrival{" "}
                        <span className="font-semibold text-slate-900">
                          {overviewEtaFormatted || "Not available"}
                        </span>
                      </span>
                    </span>
                    {!overviewEtaFormatted && (
                      <span className="max-w-[16rem] text-right text-xs font-normal text-slate-500 sm:max-w-none">
                        {overviewHasDeliveryDays
                          ? "Order date missing — ETA cannot be calculated."
                          : "No delivery window stored on this order. Contact the seller for dispatch timing."}
                      </span>
                    )}
                  </p>
                  ) : null}
                  <p className="mt-1">
                    {totalQuantity}{" "}
                    {totalQuantity === 1 ? "item" : "items"} •{" "}
                    <span className="font-semibold text-[#38513b]">
                      {formatCurrency(priceBreakdown.totalCharged)}
                    </span>
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-semibold text-slate-900">
                  Items in this order
                </h2>
                <span className="text-sm font-medium text-slate-500">
                  Fulfilled by {fulfilledByName}
                </span>
              </header>
              <div className="mt-6 space-y-4">
                {orderItems.map((item) => {
                  const canReview =
                    order.status === "Delivered" && !item.isReviewed;

                  return (
                    <article
                      key={`${item._id}-${item.name}`}
                      className="flex flex-col gap-4 rounded-2xl border border-slate-100 /60 p-4 transition hover:border-[#38513b]/30 hover:bg-white sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white">
                          {item?.images?.[0] ? (
                            <img
                              src={`${backend_url}/${item.images[0]}`}
                              alt={item.name}
                              className="h-full w-full object-contain p-3"
                            />
                          ) : (
                            <span className="text-xs font-semibold text-slate-400">
                              No image
                            </span>
                          )}
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-slate-900">
                            {item.name}
                          </h3>
                          <p className="text-sm text-slate-500">
                            Quantity:{" "}
                            <span className="font-medium text-slate-700">
                              {item.qty}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 sm:items-end">
                        <p className="text-sm font-semibold text-[#38513b]">
                          {(() => {
                            const paid = getLinePaidUnitPrice(item);
                            const was = getLineOriginalUnitPrice(item);
                            const showWas = was > paid + 0.001;
                            return (
                              <>
                                {showWas ? (
                                  <span className="mr-2 text-xs font-normal text-slate-400 line-through">
                                    {formatCurrency(was)}
                                  </span>
                                ) : null}
                                {formatCurrency(paid)}
                                <span className="ml-1 text-xs font-medium text-slate-500">
                                  each
                                </span>
                                {item?.isBundleItemFromOffer ? (
                                  <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                    Offer
                                  </span>
                                ) : null}
                              </>
                            );
                          })()}
                        </p>
                        <button
                          type="button"
                          onClick={() => handleContactSeller(item)}
                          className={secondaryButtonClasses}
                        >
                          Contact seller
                        </button>
                        {canReview && (
                          <button
                            type="button"
                            onClick={() => openReviewModal(item)}
                            className={secondaryButtonClasses}
                          >
                            Write a review
                          </button>
                        )}
                        {item.isReviewed && (
                          <span className="inline-flex h-9 items-center justify-center rounded-full bg-emerald-100 px-4 text-xs font-semibold text-emerald-700">
                            Reviewed
                          </span>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>

            {isOrderCompleted(order) && (
              <section
                id="order-reviews"
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
              >
                {!reviewExchange?.youSubmitted && (
                  <div className="mb-4 rounded-2xl border border-amber-200/80 bg-amber-50/90 px-4 py-3">
                    <p className="text-sm font-semibold text-amber-900">
                      Veteran Airsoft — leave a review
                    </p>
                    <p className="mt-1 text-sm text-amber-800">
                      Rate {fulfilledByName}. Their review of you stays hidden until you both
                      submit yours.
                    </p>
                  </div>
                )}
                {reviewExchange?.youSubmitted && !reviewExchange?.mutualComplete && (
                  <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-sm text-slate-700">
                      Thanks — your review is saved. You will see the seller&apos;s review once
                      they submit theirs.
                    </p>
                  </div>
                )}
                <h2 className="text-lg font-semibold text-slate-900">
                  Rate this seller
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Item reviews describe each product. This is your overall rating for{" "}
                  <span className="font-medium text-slate-700">{fulfilledByName}</span> — shipping,
                  communication, and service.
                </p>
                {!order.sellerReviewed ? (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={openSellerReviewModal}
                      className={primaryButtonClasses}
                    >
                      Rate seller
                    </button>
                  </div>
                ) : (
                  <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4">
                    <p className="text-sm font-medium text-slate-800">Your seller rating</p>
                    <div className="mt-2 flex items-center gap-2">
                      <Ratings rating={order.sellerReview?.rating || 0} />
                      <span className="text-sm text-slate-600">
                        {order.sellerReview?.rating != null
                          ? `${order.sellerReview.rating} out of 5`
                          : "Submitted"}
                      </span>
                    </div>
                    {order.sellerReview?.comment ? (
                      <p className="mt-2 text-sm text-slate-700">{order.sellerReview.comment}</p>
                    ) : null}
                    <p className="mt-2 text-xs text-emerald-800">Thank you for your feedback.</p>
                  </div>
                )}
                {reviewExchange?.mutualComplete && reviewExchange?.otherReview && (
                  <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                    <p className="text-sm font-medium text-slate-800">Seller&apos;s review of you</p>
                    <div className="mt-2 flex items-center gap-2">
                      <Ratings rating={reviewExchange.otherReview.rating || 0} />
                      <span className="text-sm text-slate-600">
                        {reviewExchange.otherReview.rating} out of 5
                      </span>
                    </div>
                    {reviewExchange.otherReview.comment ? (
                      <p className="mt-2 text-sm text-slate-700">
                        {reviewExchange.otherReview.comment}
                      </p>
                    ) : null}
                  </div>
                )}
              </section>
            )}

            <section className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-base font-semibold text-slate-900">
                  Shipping & UKARA details
                </h3>
                <dl className="mt-4 space-y-3 text-sm">
                  <div>
                    <dt className="text-slate-500">Recipient</dt>
                    <dd className="font-medium text-slate-900">
                      {order?.user?.name || "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Address</dt>
                    <dd className="font-medium text-slate-900">
                      {order?.shippingAddress
                        ? `${order.shippingAddress.address1 || ""} ${
                            order.shippingAddress.address2 || ""
                          }`
                        : "—"}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between text-slate-500">
                    <span>Town / City</span>
                    <span className="font-medium text-slate-900">
                      {order?.shippingAddress?.city || "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-slate-500">
                    <span>Contact</span>
                    <span className="font-medium text-slate-900">
                      {order?.user?.phoneNumber ||
                        order?.shippingAddress?.phone ||
                        "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-slate-500 pt-2 border-t border-slate-200 mt-2">
                    <span>UKARA number</span>
                    <span className="font-semibold text-slate-900">
                      {order?.ukaraNumber || order?.user?.ukaraNumber || "—"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-slate-500">
                    <span>UKARA verification</span>
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                        order?.ukaraStatus === "verified"
                          ? "bg-emerald-100 text-emerald-700"
                          : order?.ukaraStatus === "rejected"
                          ? "bg-rose-100 text-rose-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {order?.ukaraStatus === "pending" || !order?.ukaraStatus
                        ? "Pending review"
                        : order?.ukaraStatus === "verified"
                        ? "Verified"
                        : "Rejected"}
                    </span>
                  </div>
                </dl>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-base font-semibold text-slate-900">
                  Payment summary
                </h3>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between text-slate-500">
                    <span>Status</span>
                    <span className="font-medium text-slate-900">
                      {order?.paymentInfo?.status || "Not paid"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-slate-500">
                    <span>Method</span>
                    <span className="font-medium text-slate-900">
                      {order?.paymentInfo?.type || "—"}
                    </span>
                  </div>
                  <>
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>Items (bundle discounts)</span>
                      <span className="text-emerald-600 font-semibold">
                        + {formatCurrency(priceBreakdown.itemsSubtotal)}
                      </span>
                    </div>
                    {priceBreakdown.couponDiscount > 0 ? (
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <span>
                          Coupon / code discount
                          {priceBreakdown.discountCode
                            ? ` (${priceBreakdown.discountCode})`
                            : ""}
                        </span>
                        <span className="text-rose-600 font-semibold">
                          - {formatCurrency(priceBreakdown.couponDiscount)}
                        </span>
                      </div>
                    ) : null}
                    {priceBreakdown.bundleDiscount > 0 ? (
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <span>Bundle deal discount</span>
                        <span className="text-rose-600 font-semibold">
                          - {formatCurrency(priceBreakdown.bundleDiscount)}
                        </span>
                      </div>
                    ) : null}
                    {priceBreakdown.bundlePostageDiscount > 0 ? (
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <span>{bundlePostageLabelForBreakdown(priceBreakdown)}</span>
                        <span className="text-rose-600 font-semibold">
                          - {formatCurrency(priceBreakdown.bundlePostageDiscount)}
                        </span>
                      </div>
                    ) : null}
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>Buyer protection fee</span>
                      <span className="text-emerald-600 font-semibold">
                        + {formatCurrency(priceBreakdown.buyerProtectionFee)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>Postage / shipping</span>
                      <span className="text-emerald-600 font-semibold">
                        + {formatCurrency(priceBreakdown.shipping)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-slate-200 text-sm text-slate-500 mt-2">
                      <span className="text-slate-600 font-medium">Order total (charged)</span>
                      <span className="text-slate-700 font-semibold">
                        = {formatCurrency(priceBreakdown.totalCharged)}
                      </span>
                    </div>
                    {acceptedPartialRefundTotal > 0 ? (
                      <div className="flex items-center justify-between text-sm text-emerald-700 pt-2 border-t border-slate-100 mt-2">
                        <span className="font-medium">Partial refund credited</span>
                        <span className="font-semibold">
                          {formatCurrency(acceptedPartialRefundTotal)}
                          {order?.partialRefundAcceptedAt
                            ? ` · ${new Date(order.partialRefundAcceptedAt).toLocaleDateString()}`
                            : ""}
                        </span>
                      </div>
                    ) : null}
                  </>
                </dl>

                <div className="mt-6 border-t border-slate-200 pt-4">
                  <h4 className="text-sm font-semibold text-slate-900">Tracking info</h4>
                  {orderCancelled ? (
                    <div className="mt-3 rounded-2xl border-2 border-slate-300 bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-900">Order cancelled</p>
                      <p className="mt-1 text-sm text-slate-600">
                        {order?.buyerDispatchDelayChoice === "cancel"
                          ? "You cancelled this order because the seller did not ship in time. A full refund has been credited to your wallet."
                          : "This order has been cancelled. No further shipping updates are expected."}
                      </p>
                      <span className="mt-3 inline-flex items-center rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
                        Cancelled
                      </span>
                    </div>
                  ) : showDispatchDelayChoice ? (
                    <div className="mt-3 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4">
                      <p className="text-sm font-semibold text-amber-950">Seller has not shipped this order</p>
                      <p className="mt-1 text-sm text-amber-900/90">
                        The seller missed the agreed dispatch window and has not shipped after our follow-up
                        reminder. You can cancel for a full refund to your wallet, or agree to wait a little longer.
                      </p>
                      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                        <button
                          type="button"
                          onClick={() => handleDispatchDelayChoice("cancel")}
                          disabled={dispatchDelaySubmitting}
                          className={`${primaryButtonClasses} w-full justify-center sm:flex-1`}
                        >
                          {dispatchDelaySubmitting ? "Processing…" : "Cancel for full refund"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDispatchDelayChoice("wait")}
                          disabled={dispatchDelaySubmitting}
                          className={`${secondaryButtonClasses} w-full justify-center sm:flex-1`}
                        >
                          Agree to wait
                        </button>
                      </div>
                    </div>
                  ) : buyerWaitingForDispatch && !hasTrackingInfo ? (
                    <p className="mt-2 text-sm text-slate-600">
                      You agreed to wait for dispatch. The seller has been reminded to ship as soon as possible.
                    </p>
                  ) : null}
                  {!orderCancelled ? (
                  <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Status
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                          trackingStatus === "active" || hasTrackingInfo
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {trackingStatus === "active"
                          ? "Tracking active"
                          : hasTrackingInfo
                          ? "Waiting for delivery"
                          : "Waiting for tracking"}
                      </span>
                    </div>
                    {hasTrackingInfo ? (
                      <div className="mt-3 space-y-2 text-sm">
                        {trackingCourier ? (
                          <p className="text-slate-700">
                            <span className="font-semibold text-slate-900">Courier:</span>{" "}
                            {trackingCourier}
                          </p>
                        ) : null}
                        {trackingCode ? (
                          <p className="text-slate-700">
                            <span className="font-semibold text-slate-900">Code:</span>{" "}
                            <span className="font-mono">{trackingCode}</span>
                          </p>
                        ) : null}
                        {trackingLink ? (
                          <a
                            href={trackingLinkHref}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 font-semibold text-[#38513b] hover:underline break-all"
                          >
                            View shipment
                            <span aria-hidden="true">↗</span>
                          </a>
                        ) : null}
                      </div>
                    ) : (
                      <p className="mt-3 text-sm text-slate-500">
                        The seller has not uploaded tracking details yet.
                      </p>
                    )}
                  </div>
                  ) : null}
                </div>

                <div className="mt-6 border-t border-slate-200 pt-4">
                  <h4 className="text-sm font-semibold text-slate-900">Order confirmation</h4>
                  {orderCancelled ? (
                    <>
                      <p className="mt-1 text-sm text-slate-500">
                        Confirmation is not needed — this order was cancelled
                        {order?.buyerDispatchDelayChoice === "cancel"
                          ? " and refunded to your wallet"
                          : ""}
                        .
                      </p>
                      <span className="mt-2 inline-flex items-center rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
                        Cancelled
                      </span>
                    </>
                  ) : userReceivedConfirmed ? (
                    <>
                      <p className="mt-1 text-sm text-slate-500">
                        You confirmed everything is ok. Payment has been released to the seller.
                      </p>
                      <span className="mt-2 inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                        Everything is ok with my order
                      </span>
                    </>
                  ) : !hasEstimatedDelivery ? (
                    <>
                      <p className="mt-1 text-sm text-slate-500">
                        Confirmation is locked until the seller sets an estimated delivery date.
                      </p>
                      <button
                        type="button"
                        onClick={() => handleContactSeller(orderItems[0] || {})}
                        className={`${secondaryButtonClasses} mt-3 w-full justify-center`}
                      >
                        Contact seller
                      </button>
                    </>
                  ) : !canConfirmReceiptNow ? (
                    <>
                      <p className="mt-1 text-sm text-slate-500">
                        {confirmWindowEnded
                          ? `Your confirmation window ended on ${estimatedDeliveryEndDayFormatted}.`
                          : "You can confirm after the estimated delivery date."}
                      </p>
                      <p className="mt-1 text-sm text-slate-600">
                        Estimated delivery:{" "}
                        <span className="font-semibold text-slate-700">
                          {estimatedDeliveryDateFormatted || "—"}
                        </span>
                      </p>
                      <button
                        type="button"
                        onClick={() => handleContactSeller(orderItems[0] || {})}
                        className={`${secondaryButtonClasses} mt-3 w-full justify-center`}
                      >
                        Contact seller
                      </button>
                    </>
                  ) : hasOpenRefundCase ? (
                    <>
                      <p className="mt-1 text-sm text-slate-500">
                        {order.userReceivedConfirmed || order.payoutReleased
                          ? "A refund case was previously opened on this order. See the refund status below if shown."
                          : "A refund case is open on this order. See the refund status below. If you and the seller cannot agree, you can ask Veteran Airsoft to step in after 3 days."}
                      </p>
                      {payoutBlockedByRefund && (
                        <p className="mt-2 text-xs font-medium text-amber-800">
                          Payment is on hold while this refund case is open. Finish the process below or wait for a VA outcome before releasing funds to the seller.
                        </p>
                      )}
                    </>
                  ) : refundCredited ? (
                    <>
                      <p className="mt-1 text-sm text-slate-500">
                        Your refund has been credited to your wallet. No further confirmation is needed on this order.
                      </p>
                      <span className="mt-2 inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                        Refund credited
                      </span>
                    </>
                  ) : (
                    <>
                      <p className="mt-1 text-sm text-slate-500">
                        Confirm once received and issue-free to release payment to the seller.
                      </p>
                      {payoutBlockedByRefund && (
                        <p className="mt-2 text-xs font-medium text-amber-800">
                          Payment is on hold while a refund request is open. Finish that process before you can release funds to the seller.
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await axios.put(
                              `${server}/order/confirm-received/${order._id}`,
                              {},
                              { withCredentials: true }
                            );
                            toast.success("Thanks for confirming. Payment has been released to the seller.");
                            if (userId) {
                              dispatch(getAllOrdersOfUser(userId));
                            }
                          } catch (error) {
                            toast.error(
                              error?.response?.data?.message ||
                                "We couldn't save your confirmation right now."
                            );
                          }
                        }}
                        disabled={payoutBlockedByRefund}
                        className={`${primaryButtonClasses} mt-3 w-full justify-center`}
                      >
                        Everything is ok with my order
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowRefundActions((prev) => !prev)}
                        className={`${secondaryButtonClasses} mt-2 w-full justify-center`}
                      >
                        I have an issue with my order
                      </button>
                      <button
                        type="button"
                        onClick={() => handleContactSeller(orderItems[0] || {})}
                        className={`${secondaryButtonClasses} mt-2 w-full justify-center`}
                      >
                        Contact seller
                      </button>
                      {showRefundActions && (
                        <>
                          {!canRequestRefund && (
                            <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                              Refunds can only be requested within 2 days after the estimated delivery date (same window as confirming receipt). Payment stays in escrow until the refund is finished or you confirm everything is ok.
                            </p>
                          )}
                          {canRequestRefund && (
                            <p className="mt-2 text-xs text-slate-600">
                              Requesting a refund holds your payment in escrow until you and the seller resolve the case (or support steps in).
                            </p>
                          )}
                          <button
                            type="button"
                            onClick={() => openRefundModal("refund")}
                            disabled={!canRequestRefund}
                            title={refundRequestLockedReason || undefined}
                            className={`${secondaryButtonClasses} mt-2 w-full justify-center text-rose-600 hover:border-rose-500 hover:text-rose-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-400`}
                          >
                            Request a refund
                          </button>
                          <button
                            type="button"
                            onClick={() => openRefundModal("return")}
                            disabled={!canRequestRefund}
                            title={refundRequestLockedReason || undefined}
                            className={`${secondaryButtonClasses} mt-2 w-full justify-center text-amber-700 hover:border-amber-500 hover:text-amber-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-400`}
                          >
                            Return item
                          </button>
                          {refundRequestLockedReason && (
                            <p className="mt-2 text-xs text-slate-500">{refundRequestLockedReason}</p>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>

                {!(order.userReceivedConfirmed || order.payoutReleased) &&
                 !orderCancelled &&
                 !hasOpenRefundCase &&
                 !refundCaseClosed &&
                 (order.status === "Delivered" || (hasEstimatedDelivery && withinConfirmWindow && !userReceivedConfirmed)) &&
                 !ACTIVE_REFUND_STATUSES.includes(order.status || "") &&
                 !["Refund Success", "Refund Rejected", "Refund Resolved"].includes(effectiveRefundStatus) && (
                  <>
                    {!canConfirmReceiptNow && !canRequestRefund && (
                      <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <p className="text-xs text-amber-800">
                          <strong>Note:</strong> Refunds can only be requested within 2 days after the estimated delivery date.
                        </p>
                      </div>
                    )}
                    {!canConfirmReceiptNow && canRequestRefund && (
                      <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
                        <p className="text-xs text-blue-800">
                          <strong>Refund Policy:</strong> You can request a refund within 2 days after the estimated delivery date.
                        </p>
                      </div>
                    )}
                    {!canConfirmReceiptNow && (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowRefundActions((prev) => !prev)}
                          className={`${secondaryButtonClasses} mt-4 w-full justify-center`}
                        >
                          I have an issue with my order
                        </button>
                        {showRefundActions && (
                          <>
                            <button
                              type="button"
                              onClick={() => openRefundModal("refund")}
                              disabled={!canRequestRefund}
                              title={refundRequestLockedReason || undefined}
                              className={`${secondaryButtonClasses} mt-2 w-full justify-center text-rose-600 hover:border-rose-500 hover:text-rose-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-400`}
                            >
                              Request a refund
                            </button>
                            <button
                              type="button"
                              onClick={() => openRefundModal("return")}
                              disabled={!canRequestRefund}
                              title={refundRequestLockedReason || undefined}
                              className={`${secondaryButtonClasses} mt-2 w-full justify-center text-amber-700 hover:border-amber-500 hover:text-amber-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-slate-200 disabled:hover:text-slate-400`}
                            >
                              Return item
                            </button>
                            {refundRequestLockedReason && (
                              <p className="mt-2 text-xs text-slate-500">{refundRequestLockedReason}</p>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
                {(() => {
                  const hasProductRefunds = order.refunds && order.refunds.length > 0;
                  const allProductRefundsRejected = hasProductRefunds && order.refunds.every((r) => r.status === "Refund Rejected");
                  const allProductRefundsResolved = hasProductRefunds && order.refunds.every((r) => r.status === "Refund Resolved");
                  const showRejected = order.status === "Refund Rejected" || allProductRefundsRejected;
                  const showResolved = order.status === "Refund Resolved" || allProductRefundsResolved;
                  if (showResolved) {
                    return (
                      <div className="mt-6 p-4 rounded-lg bg-indigo-50 border border-indigo-200">
                        <p className="text-sm font-medium text-slate-900 mb-1">Refund request resolved</p>
                        <p className="text-sm text-slate-600">
                          Your refund request was resolved with the seller. No refund was processed (e.g. you changed your mind or reached an agreement).
                        </p>
                        <button
                          type="button"
                          onClick={() => handleContactSeller(orderItems[0] || {})}
                          className={`${secondaryButtonClasses} mt-3 w-full justify-center`}
                        >
                          Contact seller
                        </button>
                      </div>
                    );
                  }
                  if (showRejected) {
                    const orderCompleted = Boolean(order.userReceivedConfirmed || order.payoutReleased);
                    return (
                      <div className="mt-6 p-4 rounded-lg bg-rose-50 border border-rose-200">
                        <p className="text-sm font-medium text-slate-900 mb-1">Refund Status</p>
                        <p className="text-sm text-slate-600">
                          {orderCompleted
                            ? "Your refund request was declined by the seller. This order is now complete and payment has been released."
                            : "Your refund request was declined by the seller. You can ask Veteran Airsoft to step in after 3 days from your original request, or contact the seller via inbox below."}
                        </p>
                        {!(order.refunds?.length > 0) &&
                          (order.refundReason?.trim() || order.refundImages?.length > 0) && (
                          <div className="mt-3 pt-3 border-t border-rose-200">
                            {order.refundReason?.trim() ? (
                              <>
                                <p className="text-xs font-medium text-slate-700 mb-1">Your reason:</p>
                                <p className="text-xs text-slate-600 whitespace-pre-wrap">{order.refundReason}</p>
                              </>
                            ) : null}
                            <RefundEvidenceThumbnails images={order.refundImages} />
                          </div>
                        )}
                        {order.refunds && order.refunds.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-rose-200">
                            <p className="text-xs font-medium text-slate-700 mb-2">Product refunds:</p>
                            {order.refunds.map((refund, idx) => (
                              <div key={idx} className="mb-2 p-2 bg-white rounded border border-slate-200 flex justify-between items-start">
                                <div>
                                  <p className="text-xs font-semibold text-slate-900">{refund.productName}</p>
                                  <p className="text-xs text-slate-600">Qty: {refund.quantity} • {formatCurrency(refund.refundAmount)} (item value only)</p>
                                  {refund.reasonCategory && <p className="text-xs text-slate-600 mt-1">Category: {refund.reasonCategory}</p>}
                                  {refund.reason && <p className="text-xs text-slate-600 mt-1">Your message: {refund.reason}</p>}
                                  <RefundEvidenceThumbnails images={refund.images} />
                                  {refund.status === "Refund Rejected" && (refund.rejectionReason || "").trim() && (
                                    <p className="text-xs text-rose-700 mt-1 font-medium">Seller reason for declining: {refund.rejectionReason}</p>
                                  )}
                                </div>
                                <span className="text-xs font-medium text-rose-600">{refund.status === "Refund Rejected" ? "Declined" : refund.status === "Refund Resolved" ? "Resolved (no refund)" : refund.status}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleContactSeller(orderItems[0] || {})}
                          className={`${secondaryButtonClasses} mt-3 w-full justify-center`}
                        >
                          Contact seller
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}
                {(hasOpenRefundCase ||
                  ["Refund Success", "Refund Rejected", "Refund Resolved"].includes(effectiveRefundStatus)) &&
                  !(order.refunds?.length > 0 && order.refunds.every((r) => r.status === "Refund Rejected")) &&
                  !(order.refunds?.length > 0 && order.refunds.every((r) => r.status === "Refund Resolved")) && (
                  <div className="mt-6 p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <p className="text-sm font-medium text-slate-900 mb-1">Refund Status</p>
                    <p className="text-sm text-slate-600">
                      {order.status === "Processing refund" && "Your refund request is being reviewed by seller."}
                      {(order.status === "Refund Offer Pending" ||
                        pendingPartialOffers.length > 0) &&
                        "The seller offered a partial refund. Review the amount below and accept or decline."}
                      {order.status === "Return Required" &&
                        "Seller approved your return/refund path. Post the item back, then add courier and tracking below and mark as shipped. Return postage is your responsibility."}
                      {order.status === "Return in Transit" && "Return marked in transit. Waiting for seller confirmation."}
                      {order.status === "Refund Awaiting Seller Confirmation" && "Seller received return. Refund release is pending seller confirmation."}
                      {refundCredited && "Refund has been credited to your wallet."}
                    </p>
                    {(pendingPartialOffers.length > 0 || legacyPartialOfferAmount > 0) && (
                      <div className="mt-4 space-y-3 rounded-xl border border-teal-200 bg-teal-50/80 p-4">
                        <p className="text-sm font-semibold text-teal-950">Partial refund offer</p>
                        {pendingPartialOffers.map((refund) => (
                          <div key={String(refund._id)} className="rounded-lg border border-teal-100 bg-white p-3">
                            <p className="text-sm text-slate-800">
                              <span className="font-medium">{refund.productName}</span>
                              {" · "}
                              {formatCurrency(refund.sellerOfferAmount ?? refund.refundAmount)}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Item value only — VA fees are not refunded.
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <button
                                type="button"
                                disabled={offerResponseLoading}
                                onClick={() => respondToPartialOffer("accept", refund._id)}
                                className={`${primaryButtonClasses} !h-10 !px-4 text-xs`}
                              >
                                Accept refund
                              </button>
                              <button
                                type="button"
                                disabled={offerResponseLoading}
                                onClick={() => respondToPartialOffer("reject", refund._id)}
                                className={`${secondaryButtonClasses} !h-10 !px-4 text-xs text-rose-700 border-rose-200 hover:border-rose-400`}
                              >
                                Decline offer
                              </button>
                            </div>
                          </div>
                        ))}
                        {legacyPartialOfferAmount > 0 && pendingPartialOffers.length === 0 ? (
                          <div className="rounded-lg border border-teal-100 bg-white p-3">
                            <p className="text-sm text-slate-800">
                              Offered amount: {formatCurrency(legacyPartialOfferAmount)}
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <button
                                type="button"
                                disabled={offerResponseLoading}
                                onClick={() => respondToPartialOffer("accept")}
                                className={`${primaryButtonClasses} !h-10 !px-4 text-xs`}
                              >
                                Accept refund
                              </button>
                              <button
                                type="button"
                                disabled={offerResponseLoading}
                                onClick={() => respondToPartialOffer("reject")}
                                className={`${secondaryButtonClasses} !h-10 !px-4 text-xs text-rose-700 border-rose-200 hover:border-rose-400`}
                              >
                                Decline offer
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                    {canBuyerWithdrawRefund && (
                      <Link
                        to={`/user/order/${order._id}/cancel-refund`}
                        className={`${secondaryButtonClasses} mt-3 w-full justify-center border-indigo-200 text-indigo-900 hover:border-indigo-400 hover:bg-indigo-50/80`}
                      >
                        Cancel return / refund request
                      </Link>
                    )}
                    {!(order.refunds?.length > 0) &&
                      (order.refundReason?.trim() || order.refundImages?.length > 0) && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        {order.refundReason?.trim() ? (
                          <>
                            <p className="text-xs font-medium text-slate-700 mb-1">Your reason:</p>
                            <p className="text-xs text-slate-600 whitespace-pre-wrap">{order.refundReason}</p>
                          </>
                        ) : null}
                        <RefundEvidenceThumbnails images={order.refundImages} />
                      </div>
                    )}
                    {order.refunds && order.refunds.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <p className="text-xs font-medium text-slate-700 mb-2">Product Refunds:</p>
                        {order.refunds.map((refund, idx) => (
                          <div key={idx} className="mb-2 p-2 bg-white rounded border border-slate-200 flex justify-between items-start">
                            <div>
                              <p className="text-xs font-semibold text-slate-900">{refund.productName}</p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {refund.buyerDisputeKind === "return"
                                  ? "You offered to return the item"
                                  : "You requested a refund"}
                              </p>
                              <p className="text-xs text-slate-600">Qty: {refund.quantity} • Amount: {formatCurrency(refund.refundAmount)}</p>
                              <p className="text-xs text-slate-600 mt-1">{refund.reason}</p>
                              <RefundEvidenceThumbnails images={refund.images} />
                            </div>
                            <span className={`text-xs font-medium shrink-0 ml-2 ${refund.status === "Refund Rejected" ? "text-rose-600" : refund.status === "Refund Success" ? "text-green-600" : refund.status === "Refund Resolved" ? "text-indigo-600" : refund.status === "Return Required" || refund.status === "Return in Transit" || refund.status === "Refund Awaiting Seller Confirmation" ? "text-amber-700" : "text-slate-600"}`}>
                              {getRefundStatusLabel(refund.status)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(order.status === "Return Required" || order.refunds?.some((r) => r.status === "Return Required")) && (
                      <div className="mt-3 space-y-3 rounded-xl border border-slate-200 bg-white p-4">
                        <p className="text-xs font-semibold text-slate-800">Return shipment details</p>
                        <p className="text-xs text-slate-600">
                          Add courier and tracking for your return (same idea as when the seller shipped to you). Required
                          before you mark the return as shipped.
                        </p>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-slate-600">Courier</label>
                          <input
                            type="text"
                            value={returnCourier}
                            onChange={(e) => setReturnCourier(e.target.value)}
                            placeholder="e.g. Royal Mail, Evri, DPD"
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                            disabled={markingReturn}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-slate-600">Tracking number</label>
                          <input
                            type="text"
                            value={returnCode}
                            onChange={(e) => setReturnCode(e.target.value)}
                            placeholder="Tracking or reference number"
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                            disabled={markingReturn}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-slate-600">
                            Tracking link <span className="font-normal text-slate-400">(optional)</span>
                          </label>
                          <input
                            type="text"
                            value={returnLink}
                            onChange={(e) => setReturnLink(e.target.value)}
                            placeholder="https://… or leave blank"
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                            disabled={markingReturn}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            const c = returnCourier.trim();
                            const t = returnCode.trim();
                            if (!c || !t) {
                              toast.error("Please enter courier and tracking number.");
                              return;
                            }
                            const pendingReturns = (order.refunds || []).filter((r) => r.status === "Return Required");
                            const payload =
                              pendingReturns.length === 1
                                ? {
                                    refundId: pendingReturns[0]._id,
                                    returnTrackingCourier: c,
                                    returnTrackingCode: t,
                                    returnTrackingLink: returnLink.trim(),
                                  }
                                : {
                                    returnTrackingCourier: c,
                                    returnTrackingCode: t,
                                    returnTrackingLink: returnLink.trim(),
                                  };
                            setMarkingReturn(true);
                            try {
                              await axios.put(
                                `${server}/order/refund-returned/${order._id}`,
                                payload,
                                { withCredentials: true }
                              );
                              toast.success("Return marked as shipped. The seller will confirm once received.");
                              setReturnCourier("");
                              setReturnCode("");
                              setReturnLink("");
                              if (userId) dispatch(getAllOrdersOfUser(userId));
                            } catch (error) {
                              toast.error(error?.response?.data?.message || "Unable to mark return as shipped.");
                            } finally {
                              setMarkingReturn(false);
                            }
                          }}
                          disabled={markingReturn}
                          className={`${primaryButtonClasses} w-full justify-center`}
                        >
                          {markingReturn ? "Saving…" : "I have shipped the return"}
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => handleContactSeller(orderItems[0] || {})}
                      className={`${secondaryButtonClasses} mt-3 w-full justify-center`}
                    >
                      Contact seller
                    </button>
                  </div>
                )}
              </div>
            </section>

            {showVaDisputeSection && (
              <section className="rounded-3xl border border-[#38513b]/25 bg-[#f6faf7] p-6 shadow-sm sm:p-8">
                <h3 className="text-lg font-semibold text-slate-900">Need Veteran Airsoft to step in?</h3>
                <p className="mt-1 text-sm text-slate-600">
                  Work with the seller first using <strong>Contact seller</strong> or your order inbox. If you still
                  cannot agree, you can ask our team to step in after 3 days from your first refund request.
                </p>
                {order.vaMediation?.status === "resolved" && order.vaMediation?.outcomeSummary && (
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#38513b]">VA decision</p>
                    <p className="mt-1 text-sm font-medium text-slate-900">
                      {order.vaMediation.outcomeType === "full_refund" && "Full refund issued"}
                      {order.vaMediation.outcomeType === "partial_refund" && "Partial refund issued"}
                      {order.vaMediation.outcomeType === "mediation" && "Mediation / guidance"}
                      {order.vaMediation.outcomeType === "case_closed" && "Case closed"}
                      {order.vaMediation.outcomeType === "return_required" && "Return required"}
                    </p>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{order.vaMediation.outcomeSummary}</p>
                    {order.vaMediation.partialRefundAmount != null &&
                      order.vaMediation.outcomeType === "partial_refund" && (
                        <p className="mt-2 text-sm font-semibold text-slate-900">
                          Amount credited: {formatCurrency(order.vaMediation.partialRefundAmount)}
                        </p>
                      )}
                    {order.vaMediation.outcomeType === "return_required" && (
                      <p className="mt-2 text-sm text-slate-600">
                        Please submit your return tracking on this order page using the standard return steps below.
                      </p>
                    )}
                    {canAppealVaDecision && (
                      <div className="mt-4 space-y-2 border-t border-slate-200 pt-4">
                        <p className="text-xs text-slate-600">
                          You can appeal this decision within 7 days if you disagree.
                        </p>
                        <textarea
                          value={vaAppealNote}
                          onChange={(e) => setVaAppealNote(e.target.value)}
                          rows={3}
                          placeholder="Explain why you are appealing…"
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                        />
                        <button
                          type="button"
                          onClick={handleVaAppeal}
                          disabled={vaAppealing}
                          className={`${secondaryButtonClasses} w-full justify-center`}
                        >
                          {vaAppealing ? "Submitting…" : "Appeal this decision"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {isVaMediationInProgress(order.vaMediation?.status) && (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-950">
                    {order.vaMediation?.status === "mediating" ? (
                      <>
                        Veteran Airsoft is mediating this dispute privately with each party. You will see the final
                        decision here when it is issued.
                        {vaInboxLink ? (
                          <Link
                            to={vaInboxLink}
                            className="mt-3 inline-flex font-semibold text-[#38513b] underline"
                          >
                            Open your private VA chat
                          </Link>
                        ) : null}
                      </>
                    ) : (
                      <>
                        Veteran Airsoft is reviewing this dispute. We will notify you here when there is an outcome.
                      </>
                    )}
                    {order.vaMediation.buyerNote ? (
                      <p className="mt-2 text-xs opacity-90">Your note: {order.vaMediation.buyerNote}</p>
                    ) : null}
                  </div>
                )}
                {!isVaMediationInProgress(order.vaMediation?.status) &&
                  order.vaMediation?.status !== "resolved" &&
                  !(order.userReceivedConfirmed || order.payoutReleased) &&
                  effectiveVaEligibility?.canEscalate && (
                  <div className="mt-4 space-y-3">
                    <textarea
                      value={vaNote}
                      onChange={(e) => setVaNote(e.target.value)}
                      placeholder="Optional: add context for our team (what happened, what outcome you want)."
                      rows={3}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#38513b] focus:outline-none focus:ring-2 focus:ring-[#38513b]/20"
                    />
                    <button
                      type="button"
                      onClick={handleVaEscalation}
                      disabled={vaSubmitting}
                      className={`${primaryButtonClasses} w-full justify-center`}
                    >
                      {vaSubmitting ? "Submitting…" : "Ask VA to step in"}
                    </button>
                  </div>
                )}
                {!isVaMediationInProgress(order.vaMediation?.status) &&
                  order.vaMediation?.status !== "resolved" &&
                  !(order.userReceivedConfirmed || order.payoutReleased) &&
                  effectiveVaEligibility &&
                  !effectiveVaEligibility.canEscalate &&
                  effectiveVaEligibility.waitUntil &&
                  new Date(effectiveVaEligibility.waitUntil) > new Date() && (
                    <p className="mt-4 text-sm text-slate-600">
                      You can ask VA to step in from{" "}
                      <span className="font-semibold">{new Date(effectiveVaEligibility.waitUntil).toLocaleString()}</span> (3 days
                      after your refund request).
                    </p>
                  )}
              </section>
            )}

          </div>
        )}
      </div>

      {isSellerReviewModalOpen && (
        <MobileFriendlyModal
          onClose={closeSellerReviewModal}
          panelClassName="max-w-xl p-0"
          backdropClassName="backdrop-blur-sm bg-slate-900/40"
          zClass="z-20"
        >
          <div className="rounded-3xl bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Rate this seller</h2>
              <button
                type="button"
                onClick={closeSellerReviewModal}
                className="rounded-full border border-transparent p-1 text-slate-500 transition hover:border-slate-200 hover:text-slate-700"
              >
                <RxCross1 size={22} />
              </button>
            </div>
            <div className="space-y-6 px-6 py-6">
              <p className="text-sm text-slate-600">
                How was your experience with{" "}
                <span className="font-semibold text-slate-900">{fulfilledByName}</span>?
              </p>
              <div>
                <p className="text-sm font-medium text-slate-900">
                  Overall rating <span className="text-rose-500">*</span>
                </p>
                <div className="mt-2 flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((value) =>
                    sellerReviewRating >= value ? (
                      <AiFillStar
                        key={value}
                        size={26}
                        className="cursor-pointer text-amber-400"
                        onClick={() => setSellerReviewRating(value)}
                      />
                    ) : (
                      <AiOutlineStar
                        key={value}
                        size={26}
                        className="cursor-pointer text-amber-400"
                        onClick={() => setSellerReviewRating(value)}
                      />
                    )
                  )}
                </div>
              </div>
              <div>
                <label htmlFor="seller-review-comment" className="text-sm font-medium text-slate-900">
                  Comments <span className="text-slate-400">(optional)</span>
                </label>
                <textarea
                  id="seller-review-comment"
                  value={sellerReviewComment}
                  onChange={(e) => setSellerReviewComment(e.target.value)}
                  rows={4}
                  className={`mt-2 ${MODAL_TEXTAREA_CLASS} min-h-0 rounded-2xl shadow-sm`}
                  placeholder="Communication, packaging, shipping speed…"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeSellerReviewModal}
                  className={secondaryButtonClasses}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitSellerReview}
                  className={primaryButtonClasses}
                  disabled={isSubmittingSellerReview}
                >
                  {isSubmittingSellerReview ? "Submitting…" : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </MobileFriendlyModal>
      )}

      {isReviewOpen && selectedItem && (
        <MobileFriendlyModal
          onClose={closeReviewModal}
          panelClassName="max-w-xl p-0"
          backdropClassName="backdrop-blur-sm bg-slate-900/40"
          zClass="z-20"
        >
          <div className="rounded-3xl bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">
                Share your feedback
              </h2>
              <button
                type="button"
                onClick={closeReviewModal}
                className="rounded-full border border-transparent p-1 text-slate-500 transition hover:border-slate-200 hover:text-slate-700"
              >
                <RxCross1 size={22} />
              </button>
            </div>
            <div className="space-y-6 px-6 py-6">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  {selectedItem?.images?.[0] ? (
                    <img
                      src={`${backend_url}/${selectedItem.images[0]}`}
                      alt={selectedItem.name}
                      className="h-full w-full object-contain p-3"
                    />
                  ) : (
                    <span className="text-xs font-semibold text-slate-400">
                      No image
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">
                    {selectedItem?.name}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {formatCurrency(selectedItem?.discountPrice)} • Qty{" "}
                    {selectedItem?.qty}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-900">
                  Rate your experience <span className="text-rose-500">*</span>
                </p>
                <div className="mt-2 flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((value) =>
                    rating >= value ? (
                      <AiFillStar
                        key={value}
                        size={26}
                        className="cursor-pointer text-amber-400"
                        onClick={() => setRating(value)}
                      />
                    ) : (
                      <AiOutlineStar
                        key={value}
                        size={26}
                        className="cursor-pointer text-amber-400"
                        onClick={() => setRating(value)}
                      />
                    )
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="order-review-comment"
                  className="text-sm font-medium text-slate-900"
                >
                  Share more details{" "}
                  <span className="text-slate-400">(optional)</span>
                </label>
                <textarea
                  id="order-review-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  className={`mt-2 ${MODAL_TEXTAREA_CLASS} min-h-0 rounded-2xl shadow-sm`}
                  placeholder="What did you love? Anything we can improve?"
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeReviewModal}
                  className={secondaryButtonClasses}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitReview}
                  className={primaryButtonClasses}
                  disabled={isSubmittingReview}
                >
                  {isSubmittingReview ? "Submitting..." : "Submit review"}
                </button>
              </div>
            </div>
          </div>
        </MobileFriendlyModal>
      )}

      {/* Dispute/Refund Request Modal */}
      {isRefundModalOpen && (
        <MobileFriendlyModal
          onClose={() => {
            setIsRefundModalOpen(false);
            setRefundRequestMode(null);
            setRefundReason("");
            setSelectedProductsForRefund([]);
            setRefundImages({});
            setFullOrderRefundImages([]);
            setBuyerDisputeKind("refund");
          }}
          panelClassName="max-w-xl p-0 overflow-hidden"
          zClass="z-50"
          backdropClassName="bg-black/40"
        >
          <div className="overflow-y-auto rounded-2xl bg-white shadow-xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4 rounded-t-2xl">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">Request refund or return</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  {canRequestRefund ? "Within 2 days after the estimated delivery date · Refund items or entire order" : "Refund window: 2 days after the estimated delivery date"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsRefundModalOpen(false);
                  setRefundRequestMode(null);
                  setRefundReason("");
                  setSelectedProductsForRefund([]);
                  setRefundImages({});
                  setFullOrderRefundImages([]);
                  setBuyerDisputeKind("refund");
                }}
                className="p-2.5 hover:bg-slate-100 rounded-xl transition text-slate-500 hover:text-slate-700"
                disabled={isRefunding || uploadingImages}
              >
                <RxCross1 size={22} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {!canRequestRefund && (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-800">Refunds can only be requested within 2 days after the estimated delivery date.</p>
                </div>
              )}

              {canRequestRefund && (
                <>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                    <p className="text-sm font-semibold text-slate-900">What are you asking for?</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label
                        className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
                          buyerDisputeKind === "refund"
                            ? "border-[#38513b] bg-[#38513b]/10"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="buyerDisputeKind"
                          className="mt-1"
                          checked={buyerDisputeKind === "refund"}
                          onChange={() => setBuyerDisputeKind("refund")}
                          disabled={isRefunding || uploadingImages}
                        />
                        <span>
                          <span className="block text-sm font-semibold text-slate-900">Refund</span>
                          <span className="mt-1 block text-xs text-slate-600">
                            I want my money back. The seller may still ask me to return the item before they release a
                            refund.
                          </span>
                        </span>
                      </label>
                      <label
                        className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
                          buyerDisputeKind === "return"
                            ? "border-[#38513b] bg-[#38513b]/10"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="buyerDisputeKind"
                          className="mt-1"
                          checked={buyerDisputeKind === "return"}
                          onChange={() => setBuyerDisputeKind("return")}
                          disabled={isRefunding || uploadingImages}
                        />
                        <span>
                          <span className="block text-sm font-semibold text-slate-900">Return item</span>
                          <span className="mt-1 block text-xs text-slate-600">
                            I can post the item back for a refund. Return postage is my responsibility; I will add
                            courier and tracking when I ship.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>

                  {orderLineCount > 1 && (
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-slate-900">Choose refund type</p>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <button
                          type="button"
                          onClick={() => {
                            setRefundRequestMode("item");
                            setRefundReason("");
                            setFullOrderRefundImages([]);
                          }}
                          className={`inline-flex min-h-[48px] w-full items-center justify-center rounded-xl px-4 text-sm font-semibold transition ${
                            refundRequestMode === "item"
                              ? "border-2 border-[#38513b] bg-[#38513b]/10 text-[#38513b]"
                              : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                          }`}
                        >
                          Refund an item
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRefundRequestMode("full");
                            setSelectedProductsForRefund([]);
                            setRefundImages({});
                          }}
                          className={`inline-flex min-h-[48px] w-full items-center justify-center rounded-xl px-4 text-sm font-semibold transition ${
                            refundRequestMode === "full"
                              ? "border-2 border-[#38513b] bg-[#38513b]/10 text-[#38513b]"
                              : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                          }`}
                        >
                          Refund entire order
                        </button>
                      </div>
                    </div>
                  )}

                  {orderLineCount > 1 && refundRequestMode === null && (
                    <p className="text-sm text-slate-500">Select whether you are refunding specific items or the whole order, then complete the form below.</p>
                  )}

                  {(refundRequestMode === "item" && orderLineCount > 1) && (
                    <section className="space-y-3">
                      <h4 className="text-sm font-semibold text-slate-800">Items to refund</h4>
                      <p className="text-xs text-slate-500">
                        Select one or more lines, then add details and photos for each selected item. Refunds cover item value only (not shipping or VA fees).
                      </p>
                      <div className="max-h-64 space-y-3 overflow-y-auto pr-1">
                        {orderItems.map((item) => {
                          const isSelected = selectedProductsForRefund.some((r) => r.productId === item._id);
                          const lineHasRefund = productLineHasRefundRecord(order, item._id);
                          const existingRefund = order?.refunds?.find((r) => String(r.productId) === String(item._id));
                          const isRefunded = lineHasRefund;
                          const selectedRefund = selectedProductsForRefund.find((r) => r.productId === item._id);

                          return (
                            <div
                              key={item._id}
                              className={`rounded-xl border-2 transition ${
                                isSelected ? "border-[#38513b] bg-[#38513b]/5" : "border-slate-200 bg-slate-50/50 hover:border-slate-300"
                              } ${isRefunded ? "opacity-70" : ""}`}
                            >
                              <label className="flex cursor-pointer items-start gap-4 p-4">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      if (!selectedRefund) {
                                        setSelectedProductsForRefund([
                                          ...selectedProductsForRefund,
                                          {
                                            productId: item._id,
                                            productName: item.name,
                                            quantity: 1,
                                            reasonCategory: "Other",
                                            reason: "",
                                          },
                                        ]);
                                      }
                                    } else {
                                      setSelectedProductsForRefund(
                                        selectedProductsForRefund.filter((r) => r.productId !== item._id)
                                      );
                                      const newImages = { ...refundImages };
                                      delete newImages[item._id];
                                      setRefundImages(newImages);
                                    }
                                  }}
                                  disabled={isRefunded || isRefunding || !canRequestRefund}
                                  className="mt-1 h-4 w-4 rounded border-slate-300 text-[#38513b] focus:ring-[#38513b]"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-slate-900">{item.name}</p>
                                  <p className="mt-0.5 text-xs text-slate-500">
                                    Qty {item.qty} × {formatCurrency(item.discountPrice)}
                                    {isRefunded && (
                                      <span className="ml-2 text-slate-500">
                                        · {existingRefund ? getRefundStatusLabel(existingRefund.status) : "Refund on file"}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </label>

                              {isSelected && !isRefunded && (
                                <div className="mx-4 mb-4 space-y-3 rounded-lg border border-slate-200/80 bg-white p-4">
                                  {item.qty > 1 && (
                                    <div>
                                      <label className="mb-1 block text-xs font-medium text-slate-600">Quantity to refund</label>
                                      <input
                                        type="number"
                                        min="1"
                                        max={item.qty}
                                        value={selectedRefund?.quantity ?? 1}
                                        onChange={(e) => {
                                          const qty = Math.min(Math.max(1, parseInt(e.target.value, 10) || 1), item.qty);
                                          setSelectedProductsForRefund(
                                            selectedProductsForRefund.map((r) =>
                                              r.productId === item._id ? { ...r, quantity: qty } : r
                                            )
                                          );
                                        }}
                                        className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#38513b] focus:ring-2 focus:ring-[#38513b]/20"
                                        disabled={isRefunding}
                                      />
                                    </div>
                                  )}
                                  <div>
                                    <label className="mb-1 block text-xs font-medium text-slate-600">
                                      Category & details <span className="text-rose-500">*</span>
                                    </label>
                                    <select
                                      value={selectedRefund?.reasonCategory ?? "Other"}
                                      onChange={(e) => {
                                        setSelectedProductsForRefund(
                                          selectedProductsForRefund.map((r) =>
                                            r.productId === item._id ? { ...r, reasonCategory: e.target.value } : r
                                          )
                                        );
                                      }}
                                      className="mb-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#38513b] focus:ring-2 focus:ring-[#38513b]/20"
                                      disabled={isRefunding}
                                    >
                                      {REFUND_CATEGORIES.map((opt) => (
                                        <option key={opt} value={opt}>
                                          {opt}
                                        </option>
                                      ))}
                                    </select>
                                    <textarea
                                      value={selectedRefund?.reason ?? ""}
                                      onChange={(e) => {
                                        setSelectedProductsForRefund(
                                          selectedProductsForRefund.map((r) =>
                                            r.productId === item._id ? { ...r, reason: e.target.value } : r
                                          )
                                        );
                                      }}
                                      placeholder="Describe the issue (min 10 characters)…"
                                      rows={4}
                                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-[#38513b] focus:ring-2 focus:ring-[#38513b]/20"
                                      disabled={isRefunding}
                                    />
                                  </div>
                                  <div>
                                    <label className="mb-1 block text-xs font-medium text-slate-600">
                                      Photos (required, max {IMAGE_UPLOAD_MAX_MB}MB each)
                                    </label>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      multiple
                                      onChange={(e) => {
                                        const files = validateRefundImageFiles(e.target.files);
                                        if (!files) {
                                          e.target.value = "";
                                          return;
                                        }
                                        setRefundImages({ ...refundImages, [item._id]: files });
                                      }}
                                      className="block w-full text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
                                      disabled={isRefunding || uploadingImages}
                                    />
                                    {refundImages[item._id]?.length > 0 && (
                                      <p className="mt-1.5 text-xs text-slate-500">{refundImages[item._id].length} file(s) chosen</p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  )}

                  {((refundRequestMode === "full" && orderLineCount > 1) || orderLineCount <= 1) && (
                    <section className="space-y-3">
                      <h4 className="text-sm font-semibold text-slate-800">
                        {orderLineCount <= 1 ? "Refund this order" : "Refund entire order"}
                      </h4>
                      <p className="text-xs text-slate-500">
                        Describe the issue in one message (min 10 characters) and upload at least one photo. Refunds cover item value only (not shipping or VA fees).
                      </p>
                      <textarea
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        placeholder="What went wrong with your order?"
                        rows={5}
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm placeholder:text-slate-400 focus:border-[#38513b] focus:ring-2 focus:ring-[#38513b]/20"
                        disabled={isRefunding}
                      />
                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-600">
                          Photos (required, max {IMAGE_UPLOAD_MAX_MB}MB each)
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = validateRefundImageFiles(e.target.files);
                            if (!files) {
                              e.target.value = "";
                              return;
                            }
                            setFullOrderRefundImages(files);
                          }}
                          className="block w-full text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
                          disabled={isRefunding || uploadingImages}
                        />
                        {fullOrderRefundImages.length > 0 && (
                          <p className="mt-1.5 text-xs text-slate-500">{fullOrderRefundImages.length} file(s) chosen</p>
                        )}
                      </div>
                    </section>
                  )}

                  <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setIsRefundModalOpen(false);
                        setRefundRequestMode(null);
                        setRefundReason("");
                        setSelectedProductsForRefund([]);
                        setRefundImages({});
                        setFullOrderRefundImages([]);
                        setBuyerDisputeKind("refund");
                      }}
                      className="rounded-xl border border-slate-200 px-5 py-2.5 font-medium text-slate-700 transition hover:bg-slate-50"
                      disabled={isRefunding || uploadingImages}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={() => requestRefund(refundRequestMode === "item" ? "selected" : "full")}
                      className="rounded-xl bg-[#38513b] px-5 py-2.5 font-semibold text-white transition hover:bg-[#2f4232] disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={
                        isRefunding ||
                        uploadingImages ||
                        !canRequestRefund ||
                        (orderLineCount > 1 && refundRequestMode === null)
                      }
                    >
                      {isRefunding || uploadingImages ? "Submitting…" : "Submit refund request"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </MobileFriendlyModal>
      )}
    </div>
  );
};

export default UserOrderDetails;
